import { set, get, first, chain, size, find, reduce, replace, orderBy, isNumber, filter, pick, map, omit, findLast } from "lodash"
import pluralize from "pluralize"
import { Info } from "@mui/icons-material"
import Tooltip from "components/material/tooltip"
import { makeCurrencyFormatter, numberFormatter } from "helpers/formatters/numbers"
import { getRollupSummaryLabel } from "helpers/rollups"
import { createSelector } from "reselect"
import { VIEW_TYPES } from "helpers/base"
import { sanitizeBasketTicker } from "helpers/swaps"

const EMPTY_ARRAY = []

function getGroupSummaryText(groups) {
  const numberOfSecurities = get(first(groups), "contributors.length", 0)
  // [TODO] [#184018795] JSX shouldn't be in selectors or models
  return <span className="summary-text">
    {numberOfSecurities} {pluralize("Security", numberOfSecurities)}
  </span>
}

function getBasketSummaryText(basketContributors) {
  const numberOfBasketContributors = size(basketContributors)
  return (
    <span className="summary-text">
      {numberOfBasketContributors} {pluralize("Security", numberOfBasketContributors)}
    </span>
  )
}

function getContributorsGroupSummaryText({ groups, contributorsGrouping, gmvLabel }) {
  return (
    <span className="summary-text">
      {groups.length} {pluralize(getRollupSummaryLabel(contributorsGrouping), groups.length)}{gmvLabel}
    </span>
  )
}

export function contributorsSummaryTextElem({
  contributorsGrouping,
  contributors,
  groups,
  selectedGroup,
  selectedBasket,
  basketContributors,
  positionsDisparity,
  referenceEquity,
  gmv,
  currency,
}) {
  const dollarFormatter = makeCurrencyFormatter(currency)
  let gmvLabel
  if (referenceEquity) {
    gmvLabel = <span>
      {` | ${dollarFormatter(referenceEquity)}  Ref equity`}
      <Tooltip placement="right" title={`${dollarFormatter(gmv)}  GMV`}>
        <Info className="summary-gmv-icon" />
      </Tooltip>
    </span>
  } else if (gmv) {
    gmvLabel = ` | ${dollarFormatter(gmv)}  GMV`
  }

  if (selectedGroup) return getGroupSummaryText(groups)
  if (contributorsGrouping) return getContributorsGroupSummaryText({ groups, contributorsGrouping, gmvLabel })
  if (selectedBasket) return getBasketSummaryText(basketContributors)

  const disparityLabel = positionsDisparity ? "Unique " : ""

  return (
    <span className="summary-text">
      {contributors.length} {disparityLabel}{pluralize("Security", contributors.length)}{gmvLabel}
    </span>
  )
}

export function positionSetContributorsSummaryText(contributorsGrouping, contributors, groups, selectedGroup, showNotHeld, viewType, positionSetType = "portfolio") {
  if (contributorsGrouping && !selectedGroup) {
    return `${groups.length} ${pluralize(getRollupSummaryLabel(contributorsGrouping), groups.length)}`
  }

  let contributorList = contributors
  if (selectedGroup) {
    contributorList = get(first(groups), "contributors")
  }

  let numberOfSecurities = size(contributorList)
  if (viewType === VIEW_TYPES.ACTIVE && !showNotHeld) {
    numberOfSecurities = chain(contributorList)
      .filter(item => item.id !== "NOT HELD")
      .size()
      .value()
  }
  const middlePart = showNotHeld && viewType === VIEW_TYPES.ACTIVE ? "active" : `${positionSetType}${viewType === VIEW_TYPES.ACTIVE ? " active" : ""}`
  return `${numberOfSecurities} ${middlePart} ${pluralize("contributor", numberOfSecurities)}`
}

export function makeContributorsGroupDropdownSelector(columns) {
  const getSelectedCategory = (_, props) => get(props, "selectedOption.label")
  const getSelected = (_, props) => get(props, "selected")
  const getSelectedGroup = (_, props) => get(props, "selectedGroup", {})
  const getContributorsGrouping = (_, props) => get(props, "contributorsGrouping")

  return createSelector(
    [getSelected, getSelectedCategory, getSelectedGroup, getContributorsGrouping],
    (selected, selectedCategory, selectedGroup, contributorsGrouping) => {
      const { group, details } = columns
      if (selectedGroup) {
        return set([...group], "0.label", get(contributorsGrouping, "label"))
      }
      if (selected) {
        return set([...details], "0.label", selectedCategory)
      }

      return group
    },
  )
}

/**
 * Assigns attributes from second contributor into the first. Mutates the first contributor.
 */
export function combineContributors(first, second) {
  if (first.assetClass === "swap") {
    first.id = sanitizeBasketTicker(second.id)
    first.economicExposure += Math.abs(second.economicExposure)
    first.description = first.description ?? second.description
  } else {
    first.economicExposure += second.economicExposure
  }
}

// Only the composition screen combines split unmodeled baskets but this is also used by asset view
// hence the combineSplitUnmodeledBaskets parameter
export function compareIdentifier(position1, position2, combineSplitUnmodeledBaskets = false) {
  let pos1Id = position1.id
  let pos2Id = position2.id
  if (combineSplitUnmodeledBaskets) {
    pos1Id = sanitizeBasketTicker(position1.id)
    pos2Id = sanitizeBasketTicker(position2.id)
  }

  return position2.modelProviderId
    ? position1.modelProviderId === position2.modelProviderId
    // Both modelProviderId should be undefined then
    : position1.modelProviderId === position2.modelProviderId && pos1Id === pos2Id && position1.assetClass === position2.assetClass
}

export function findItemByIdentifier(collection, item, combineSplitUnmodeledBaskets) {
  return find(collection, value => compareIdentifier(value, item, combineSplitUnmodeledBaskets))
}

export function combineDuplicateContributors(positions, combineSplitUnmodeledBaskets = false) {
  return reduce(positions, (acc, position) => {
    const duplicateItem = findItemByIdentifier(acc, position, combineSplitUnmodeledBaskets)
    if (!duplicateItem) {
      acc.push(position)
    } else {
      combineContributors(duplicateItem, position)
    }
    return acc
  }, [])
}

export function combinePositionsIncludingSplitBasket(assets) {
  if (!assets) {
    return undefined
  }
  return combineDuplicateContributors(assets, true)
}

export function sortContributors(contributors, selectedField, order) {
  return orderBy(contributors, [
    (security) => {
      switch (order.by) {
        case "name":
          return security.id
        case "equity":
          return order.abs ? Math.abs(security.size) : security.size
        default: {
          const value = get(security, selectedField)
          return order.abs && isNumber(value) ? Math.abs(value) : value
        }
      }
    },
  ], [order.dir])
}

export function formatBetaInstrumentTooltip(betaInstrument) {
  if (!betaInstrument) {
    return null
  }

  // The keys below are for styling the tooltip
  // dynamicBetaId = bold
  // dynamicBetaDescription = regular, smaller font
  const { name, description, id, ticker, mic } = betaInstrument
  switch (get(betaInstrument, "type")) {
    case "PORTFOLIO":
    case "INDEX":
      return {
        dynamicBetaId: name,
        dynamicBetaDescription: description,
      }
    case "SWAP":
      // Swaps don't have a name
      return {
        dynamicBetaId: id,
        dynamicBetaDescription: description,
      }
    case "ETF":
      return {
        dynamicBetaId: ticker && mic && `${ticker}.${mic}`,
        dynamicBetaDescription: name,
      }
    case "BENCHMARK":
      return {
        dynamicBetaId: id,
        dynamicBetaDescription: name,
      }
  }
  return betaInstrument
}

export const buildContributorKey = ({ modelProviderId, assetClass, id, identifier }) => {
  let key
  if (modelProviderId) {
    key = modelProviderId
  } else if (assetClass) {
    key = `${assetClass}_${id || identifier}`
  } else {
    key = id || identifier
  }
  return replace(key, /\./g, "_")
}

export function computeSelected(selected, items) {
  if (!selected) {
    return selected
  }

  const found = find(items, d => d.id === selected.id)
  if (found) {
    return selected
  } else {
    return null
  }
}

export function computeSelectedValue(contributors, selected) {
  if (!selected) {
    return 0
  }

  const item = find(contributors, d => d.id === selected.id)
  if (item) {
    return item.value
  } else {
    return 0
  }
}

export function computeSortedContributors(data, selectedField, order) {
  if (data.length === 0) return EMPTY_ARRAY

  return orderBy(data, [
    (security) => {
      switch (order.by) {
        case "name":
          return security.id
        case "equity":
          return order.abs ? Math.abs(security.size) : security.size
        default: {
          const value = get(security, selectedField)
          return order.abs && isNumber(value) ? Math.abs(value) : value
        }
      }
    },
  ], [order.dir])
}

export function computeSelectedGroupValue(selectedGroup, groupedContributors, selectedField) {
  if (selectedGroup) {
    const group = find(groupedContributors, { id: selectedGroup.id })
    return get(group, selectedField)
  }

  return null
}

export function computeSelectedGroupSiblings(selectedGroup, groupedContributors, selectedField) {
  if (selectedGroup) {
    return chain(groupedContributors)
      .map(group => ({
        ...group,
        value: get(group, selectedField),
      }))
      .orderBy(["name"], ["asc"])
      .value()
  }

  return null
}

export function computeFilteredGroupedContributors(data, selectedField, selectedGroup) {
  return chain(data)
    .filter(group => !selectedGroup || selectedGroup.id === group.id)
    .map(group => ({
      ...group,
      contributors: filter(group.contributors, security => get(security, selectedField)),
    }))
    .value()
}

export function computeSortedGroupContributors(data, selectedField, order) {
  if (data.length === 0) return EMPTY_ARRAY

  return chain(data)
    .map(group => ({
      ...group,
      value: get(group, selectedField),
      contributors: orderBy(group.contributors, [
        (security) => {
          switch (order.by) {
            case "name":
              return security.id
            case "equity":
              return order.abs ? Math.abs(security.size) : security.size
            default: {
              const value = get(security, selectedField)
              return order.abs && isNumber(value) ? Math.abs(value) : value
            }
          }
        },
      ], [order.dir]),
    }))
    .orderBy([
      (group) => {
        switch (order.by) {
          case "name":
            return group.name
          case "equity":
            return order.abs ? Math.abs(group.totalPercentEquity) : group.totalPercentEquity
          default:
            return order.abs ? Math.abs(group.value) : group.value
        }
      },
    ], [order.dir])
    .value()
}

export function computeContributorFormatter({ formatter, noPercent }) {
  // If a formatter is passed in, use it
  if (formatter) { return formatter }
  // Otherwise check if the value needs to be multiplied by 100 or not
  if (noPercent) {
    return numberFormatter
  }
  return value => numberFormatter(value * 100)
}

export function extractContributorRisk(contributor) {
  return {
    ...pick(contributor, ["id", "assetClass", "modelProviderId", "description", "notHeld"]),
    risk: {
      total: get(contributor, ["attribution", "summary", "factors"]) + get(contributor, ["attribution", "summary", "specific"]),
      ...get(contributor, ["attribution", "summary"]),
    },
  }
}

/**
 * Maps nested return cumulative % fields to top level fields for performance contributors
 */
export function mapPerformanceContributorsReturnsToValues(contributors, performanceMetricField = "cumulativePercent") {
  return map(contributors, contributor => ({
    ...contributor,
    total: get(contributor, ["totalReturn", performanceMetricField]),
    attribution: {
      ...contributor.attribution,
      factors: mapPerformanceFactorsReturnsToValue(contributor.attribution.factors, performanceMetricField),
    },
  }))
}

export function mapPerformanceFactorsReturnsToValue(factors, performanceMetricField = "cumulativePercent") {
  return map(factors, factor => ({
    ...omit(factor, "return"),
    value: get(factor, ["return", performanceMetricField]),
  }))
}

export function mapPerformanceAttributionFactorReturnsToValues(attributionDates, performanceMetricField) {
  return map(attributionDates, attributionDate => ({
    ...attributionDate,
    attribution: {
      ...attributionDate.attribution,
      factors: mapPerformanceFactorsReturnsToValue(attributionDate.attribution.factors, performanceMetricField),
    },
  }))
}

export function getLastContributor(dailyContributors, date) {
  return findLast(dailyContributors, dayContributors => dayContributors?.date?.format("YYYY-MM-DD") <= date) || null
}

export function getBetaInstrumentLabelAndTooltip(betaInstrument) {
  if (!betaInstrument.type) {
    // We shouldn't reach this point, but this is to avoid an empty label if something goes wrong
    return {
      label: "Custom",
    }
  }
  const formattedBetaInstrumentTooltip = formatBetaInstrumentTooltip(betaInstrument)
  const descriptor = formattedBetaInstrumentTooltip.dynamicBetaId ? ` - ${formattedBetaInstrumentTooltip.dynamicBetaId}` : ""
  return {
    label: `Custom${descriptor}`,
    tooltipContents: formattedBetaInstrumentTooltip,
  }
}
